// use-auth — React-Hook für Sign-In / Sign-Up / Session-Hydratisierung mit
// Auto-Refresh + Owner-Override. V1-Pendant: src/hooks/useAuth.js.
//
// Architektur:
// - State lebt im `auth-store` (Zustand + persist).
// - API-Calls gehen über `lib/auth-api.ts` (testbar via fetch-Mock).
// - Auto-Refresh + Profile-Hydrate beim Mount via `useEffect`.
// - Owner-Override: VITE_OWNER_EMAIL aus env wird gegen Profil-Email gematcht.

import { useCallback, useEffect } from 'react';
import {
  fetchProfile as fetchProfileApi,
  recoverPassword as recoverPasswordApi,
  refreshSession as refreshSessionApi,
  signIn as signInApi,
  signUp as signUpApi,
  updateProfile as updateProfileApi,
} from '@/lib/auth-api';
import { defaultNicknameFromEmail, isSessionExpiringSoon } from '@/lib/session';
import {
  selectAccessToken,
  selectIsAuthenticated,
  selectIsOwner,
  selectProfile,
  selectSession,
  useAuthStore,
} from '@/stores/auth-store';
import type { AuthSession, UserProfile, UserProfilePatch, UserRole } from '@/types/auth';

function getOwnerEmail(): string | null {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OWNER_EMAIL) {
      return import.meta.env.VITE_OWNER_EMAIL;
    }
  } catch {
    // ignore
  }
  return null;
}

export interface UseAuthReturn {
  session: AuthSession | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string;
  isAuthenticated: boolean;
  isOwner: boolean;
  accessToken: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, nickname: string, startTrial?: boolean) => Promise<void>;
  signOut: () => void;
  resetPassword: (email: string) => Promise<boolean>;
  reloadProfile: () => Promise<void>;
  patchProfile: (patch: UserProfilePatch) => Promise<void>;
  setError: (msg: string) => void;
}

/**
 * Lädt Profil und überschreibt loading/profile im Store. Wird beim Mount,
 * nach Sign-In und nach erfolgreichem Refresh aufgerufen.
 */
async function hydrateProfile(session: AuthSession): Promise<void> {
  const store = useAuthStore.getState();
  try {
    let profile = await fetchProfileApi(session.access_token);
    if (!profile) {
      // V1-Fallback: minimales Profil aus Session-User bauen
      profile = {
        id: session.user.id,
        email: session.user.email,
        nickname: defaultNicknameFromEmail(session.user.email),
        role: 'user' as UserRole,
      };
    }
    const ownerEmail = getOwnerEmail();
    if (ownerEmail && profile.email === ownerEmail) {
      profile = { ...profile, role: 'owner' };
    }
    store.setProfile(profile);
  } catch (e) {
    console.warn('[auth] Profile load failed:', e instanceof Error ? e.message : e);
    store.setProfile({
      id: session.user.id,
      email: session.user.email,
      nickname: defaultNicknameFromEmail(session.user.email),
      role: 'user',
    });
  } finally {
    store.setLoading(false);
  }
}

export function useAuth(): UseAuthReturn {
  const session = useAuthStore(selectSession);
  const profile = useAuthStore(selectProfile);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isOwner = useAuthStore(selectIsOwner);
  const accessToken = useAuthStore(selectAccessToken);

  // Mount-Effect: Session prüfen, ggf. refreshen, dann Profil laden.
  useEffect(() => {
    const store = useAuthStore.getState();
    const current = store.session;
    if (!current) {
      store.setLoading(false);
      return;
    }
    if (isSessionExpiringSoon(current)) {
      refreshSessionApi({ refresh_token: current.refresh_token })
        .then((refreshed) => {
          store.beginSession(refreshed, store.sessionStartedAt ?? Date.now());
          return hydrateProfile(refreshed);
        })
        .catch((e) => {
          console.warn('[auth] Refresh failed:', e instanceof Error ? e.message : e);
          store.endSession();
          store.setLoading(false);
        });
    } else {
      void hydrateProfile(current);
    }
    // Mount-only: kein Re-Run bei session-changes (das macht signIn manuell).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const store = useAuthStore.getState();
    store.setError('');
    store.setLoading(true);
    try {
      const data = await signInApi({ email, password });
      store.beginSession(data, Date.now());
      // Fire-and-forget last_login_at update (V1-Verhalten).
      void updateProfileApi(data.access_token, {
        last_login_at: new Date().toISOString(),
      }).catch(() => undefined);
      await hydrateProfile(data);
    } catch (e) {
      store.setError(e instanceof Error ? e.message : 'Sign-In fehlgeschlagen');
      store.setLoading(false);
    }
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, nickname: string, startTrial = false) => {
      const store = useAuthStore.getState();
      store.setError('');
      store.setLoading(true);
      try {
        await signUpApi({ email, password, nickname, trial: startTrial });
        store.setError(
          'SUCCESS: Bestätigungs-E-Mail wurde gesendet. Bitte prüfe dein Postfach und klicke auf den Link.',
        );
      } catch (e) {
        store.setError(e instanceof Error ? e.message : 'Sign-Up fehlgeschlagen');
      } finally {
        store.setLoading(false);
      }
    },
    [],
  );

  const signOut = useCallback(() => {
    useAuthStore.getState().endSession();
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    const store = useAuthStore.getState();
    store.setError('');
    try {
      await recoverPasswordApi(email);
      return true;
    } catch (e) {
      store.setError(e instanceof Error ? e.message : 'Passwort-Reset fehlgeschlagen');
      return false;
    }
  }, []);

  const reloadProfile = useCallback(async () => {
    const current = useAuthStore.getState().session;
    if (!current) return;
    await hydrateProfile(current);
  }, []);

  const patchProfile = useCallback(async (patch: UserProfilePatch) => {
    const store = useAuthStore.getState();
    const token = store.session?.access_token;
    if (!token) throw new Error('Nicht eingeloggt');
    const updated = await updateProfileApi(token, patch);
    store.setProfile(updated);
  }, []);

  const setError = useCallback((msg: string) => {
    useAuthStore.getState().setError(msg);
  }, []);

  return {
    session,
    profile,
    loading,
    error,
    isAuthenticated,
    isOwner,
    accessToken,
    signIn,
    signUp,
    signOut,
    resetPassword,
    reloadProfile,
    patchProfile,
    setError,
  };
}
